import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Link,
  Typography,
  CircularProgress,
} from '@mui/material';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import type { EnvironmentDocument, AddEnvironmentRequest, UpdateEnvironmentRequest } from '@finvari/deliver';
import { effectiveGuardrails } from '@finvari/deliver';
import { addEnvironmentToConfig, updateEnvironment } from '../firebase';
import { useConfig } from '../contexts/ConfigContext';
import { usePools } from '../contexts/PoolsContext';

interface EnvironmentFormDialogProps {
  open: boolean;
  onClose: () => void;
  // Omit to create a new environment; pass the document to edit an existing one.
  environment?: EnvironmentDocument;
  onSaved?: () => void;
}

interface UserOption {
  uid: string;
  email: string;
}

const NO_OWNER = '__none__';
const NO_POOL = '__none__';

export default function EnvironmentFormDialog({ open, onClose, environment, onSaved }: EnvironmentFormDialogProps) {
  const { userProfile } = useConfig();
  const { pools } = usePools();
  const canEditConfig = userProfile?.canEditConfig ?? false;
  const myUid = userProfile?.uid;
  const isEdit = !!environment;

  const [gcpProjectId, setGcpProjectId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [canResetData, setCanResetData] = useState(true);
  const [canLoadCustomerData, setCanLoadCustomerData] = useState(false);
  const [ownerUid, setOwnerUid] = useState<string>(NO_OWNER);
  const [poolId, setPoolId] = useState<string>(NO_POOL);
  // Admins toggle between the "personal (owned by me)" checkbox and the assign-to-another dropdown.
  const [assignToOther, setAssignToOther] = useState(false);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);

  // Seed the form whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    setGcpProjectId(environment?.gcpProjectId ?? '');
    setName(environment?.name ?? '');
    setDescription(environment?.description ?? '');
    setCanResetData(environment?.canResetData !== false); // default true
    setCanLoadCustomerData(environment?.canLoadCustomerData === true); // default false
    setOwnerUid(environment?.ownerUid ?? NO_OWNER);
    setPoolId(environment?.poolId ?? NO_POOL);
    // Start in dropdown mode when the env is already owned by someone other than the current user.
    setAssignToOther(!!environment?.ownerUid && environment.ownerUid !== myUid);
    setError(null);
    setConfirmArchive(false);
  }, [open, environment, myUid]);

  // Config editors can assign any user as owner — load the user list directly (the relaxed
  // userProfile read rule allows this for canEditConfig holders).
  useEffect(() => {
    if (!open || !canEditConfig) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(getFirestore(), 'userProfile'));
        const list: UserOption[] = snap.docs
          .map((d) => ({ uid: d.id, email: (d.data() as { email?: string }).email ?? d.id }))
          .sort((a, b) => a.email.localeCompare(b.email));
        if (!cancelled) setUsers(list);
      } catch (e) {
        // Non-fatal: the dropdown just won't be populated.
        console.warn('Could not load user list for owner dropdown:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, canEditConfig]);

  const isPersonalMine = ownerUid !== NO_OWNER && ownerUid === myUid;

  const handleSave = async () => {
    const trimmedId = gcpProjectId.trim();
    if (!isEdit && !trimmedId) {
      setError('GCP Project ID is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Owner and pool are mutually exclusive: joining a pool clears any owner. Only config editors
      // can change pool membership, so leave poolId unset (undefined ⇒ unchanged) otherwise.
      const joiningPool = canEditConfig && poolId !== NO_POOL;
      const resolvedPoolId = canEditConfig ? (poolId === NO_POOL ? null : poolId) : undefined;
      // A callable encodes an explicitly-undefined property as null, and null reads as "leave the
      // pool" — so omit the key entirely rather than sending undefined for "membership unchanged".
      const poolPatch = resolvedPoolId !== undefined ? { poolId: resolvedPoolId } : {};
      const resolvedOwnerUid = joiningPool ? null : ownerUid === NO_OWNER ? null : ownerUid;
      // Pooled environments inherit their guardrails from the pool (the checkboxes are hidden). Send
      // the pool's values so the doc matches; the backend also forces/ignores them for pooled envs.
      const guardrails =
        poolId !== NO_POOL
          ? effectiveGuardrails(pools.find((p) => p.poolId === poolId))
          : { canResetData, canLoadCustomerData };
      if (isEdit) {
        const payload: UpdateEnvironmentRequest = {
          gcpProjectId: environment!.gcpProjectId,
          name: name.trim() || environment!.gcpProjectId,
          description: description.trim(),
          ...guardrails,
          ownerUid: resolvedOwnerUid,
          ...poolPatch,
        };
        await updateEnvironment(payload);
      } else {
        const payload: AddEnvironmentRequest = {
          gcpProjectId: trimmedId,
          name: name.trim() || undefined,
          description: description.trim() || undefined,
          ...guardrails,
          ownerUid: resolvedOwnerUid,
          ...poolPatch,
        };
        await addEnvironmentToConfig(payload);
      }
      onSaved?.();
      onClose();
    } catch (e: any) {
      setError(e?.message || `Failed to ${isEdit ? 'update' : 'create'} environment`);
    } finally {
      setSubmitting(false);
    }
  };

  // Archiving is a soft delete gated by the same edit permission; unarchive restores visibility.
  const isArchived = !!environment?.archived;
  const handleArchiveToggle = async () => {
    if (!isEdit) return;
    setSubmitting(true);
    setError(null);
    try {
      await updateEnvironment({ gcpProjectId: environment!.gcpProjectId, archived: !isArchived });
      onSaved?.();
      onClose();
    } catch (e: any) {
      setConfirmArchive(false);
      setError(e?.message || `Failed to ${isArchived ? 'unarchive' : 'archive'} environment`);
    } finally {
      setSubmitting(false);
    }
  };

  // Owner section: a plain personal-checkbox for everyone, with an admin-only escape hatch to
  // assign a different user via a dropdown.
  const ownerSection =
    canEditConfig && assignToOther ? (
      <Box>
        <FormControl fullWidth size="small" disabled={submitting}>
          <InputLabel id="environment-owner-label">Owner</InputLabel>
          <Select
            labelId="environment-owner-label"
            value={ownerUid}
            label="Owner"
            onChange={(e) => setOwnerUid(e.target.value)}
          >
            <MenuItem value={NO_OWNER}>No owner (shared)</MenuItem>
            {users.map((u) => (
              <MenuItem key={u.uid} value={u.uid}>
                {u.email}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Link
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          disabled={submitting}
          onClick={() => {
            setAssignToOther(false);
            setOwnerUid(myUid ?? NO_OWNER);
          }}
          sx={{ mt: 0.5 }}
        >
          Make this a personal environment
        </Link>
      </Box>
    ) : (
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={isPersonalMine}
              onChange={(e) => setOwnerUid(e.target.checked ? (myUid ?? NO_OWNER) : NO_OWNER)}
              disabled={submitting}
            />
          }
          label="Personal dev environment (owned by me)"
        />
        {canEditConfig && (
          <Box>
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              disabled={submitting}
              onClick={() => setAssignToOther(true)}
            >
              Assign to another user
            </Link>
          </Box>
        )}
      </Box>
    );

  // Whether the environment is in a pool right now (assigned already, or being assigned). Used to
  // hide the per-environment guardrail checkboxes for *any* editor — a pooled env's guardrails are
  // owned by its pool. (The pool *dropdown* itself remains config-editor-only, below.)
  const hasPool = poolId !== NO_POOL;
  const selectedPool = pools.find((p) => p.poolId === poolId);
  const poolSection = canEditConfig ? (
    <FormControl fullWidth size="small" disabled={submitting}>
      <InputLabel id="environment-pool-label">Pool</InputLabel>
      <Select
        labelId="environment-pool-label"
        value={poolId}
        label="Pool"
        onChange={(e) => {
          const value = e.target.value;
          setPoolId(value);
          if (value !== NO_POOL) setOwnerUid(NO_OWNER);
        }}
      >
        <MenuItem value={NO_POOL}>Not in a pool</MenuItem>
        {pools.map((p) => (
          <MenuItem key={p.poolId} value={p.poolId}>
            {p.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ) : null;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? `Edit Environment — ${environment!.gcpProjectId}` : 'New Environment'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label="GCP Project ID"
            value={gcpProjectId}
            onChange={(e) => setGcpProjectId(e.target.value)}
            fullWidth
            required={!isEdit}
            disabled={isEdit || submitting}
            helperText={isEdit ? undefined : 'Used as both the environment ID and the GCP Project ID'}
          />
          <TextField
            label="Display Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            disabled={submitting}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            disabled={submitting}
          />

          {poolSection}

          {hasPool ? (
            <Typography variant="body2" color="text.secondary">
              Pooled environments have no owner — they are leased through the pool queue.
            </Typography>
          ) : (
            ownerSection
          )}

          {/* Guardrails are per-environment normally, but a pooled environment inherits them from
              its pool — hide the checkboxes and show what it will inherit. */}
          {hasPool ? (
            <Typography variant="body2" color="text.secondary">
              Data guardrails are inherited from the pool: wiping is{' '}
              {effectiveGuardrails(selectedPool).canResetData ? 'allowed' : 'disabled'}, customer-snapshot
              access is {effectiveGuardrails(selectedPool).canLoadCustomerData ? 'on' : 'off'}.
            </Typography>
          ) : (
            <>
              {/* Wrapped in a Box so the checkbox left edge matches the owner checkbox above
                  (a bare FormControlLabel becomes a stretched flex item and shifts left). */}
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={canResetData}
                      onChange={(e) => setCanResetData(e.target.checked)}
                      disabled={submitting}
                    />
                  }
                  label="Allow database to be wiped"
                />
              </Box>
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={canLoadCustomerData}
                      onChange={(e) => setCanLoadCustomerData(e.target.checked)}
                      disabled={submitting}
                    />
                  }
                  label="Has access to customer snapshots (requires manual IAM grant)"
                />
              </Box>
            </>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {isEdit && (
          <>
            <Button
              color={isArchived ? 'primary' : 'warning'}
              onClick={isArchived ? handleArchiveToggle : () => setConfirmArchive(true)}
              disabled={submitting}
            >
              {isArchived ? 'Unarchive' : 'Archive'}
            </Button>
            <Box sx={{ flex: 1 }} />
          </>
        )}
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={submitting || (!isEdit && !gcpProjectId.trim())}
          startIcon={submitting ? <CircularProgress size={20} /> : undefined}
        >
          {submitting ? 'Saving...' : isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>

      {/* Confirm before archiving (soft delete); unarchiving needs no confirmation. */}
      <Dialog open={confirmArchive} onClose={submitting ? undefined : () => setConfirmArchive(false)}>
        <DialogTitle>Archive environment?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            "{environment?.gcpProjectId}" will be hidden from the environment list and deploy pickers. You can
            unarchive it later.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmArchive(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={handleArchiveToggle}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : undefined}
          >
            Archive environment
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
